/**
 * SEMANTIC TOKENS — amaca yönelik isimler.
 * UI'da yalnızca bu katmanı çağırırsın. Light + Dark için iki harita.
 * AntD ConfigProvider ve CSS variables aynı dosyadan beslenir.
 */
import { palette, elevation, elevationDark } from './primitives';

export interface SemanticPalette {
  // Brand
  brand: string;
  brandHover: string;
  brandActive: string;
  brandSubtle: string;
  brandContrast: string;

  // Backgrounds
  bgCanvas: string;
  bgElevated: string;
  bgSubtle: string;
  bgMuted: string;
  bgInverse: string;

  // Surfaces (specific)
  sidebarBg: string;
  sidebarItemHoverBg: string;
  sidebarItemActiveBg: string;
  sidebarItemActiveAccent: string;
  topbarBg: string;
  cardBg: string;

  // Text
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textDisabled: string;
  textOnBrand: string;
  textOnInverse: string;

  // Borders
  borderSubtle: string;
  borderDefault: string;
  borderStrong: string;
  borderFocus: string;

  // Status
  success: string;
  successSubtle: string;
  warning: string;
  warningSubtle: string;
  danger: string;
  dangerSubtle: string;
  info: string;
  infoSubtle: string;

  // Content status (Strapi-style)
  statusDraft: string;
  statusReview: string;
  statusPublished: string;
  statusArchived: string;

  // Elevation
  shadow1: string;
  shadow2: string;
  shadow3: string;
  shadow4: string;
  shadow5: string;
}

export const semanticLight: SemanticPalette = {
  brand: palette.green[500],
  brandHover: palette.green[600],
  brandActive: palette.green[700],
  brandSubtle: palette.green[50],
  brandContrast: palette.gray[0],

  bgCanvas: palette.gray[50],
  bgElevated: palette.gray[0],
  bgSubtle: palette.gray[25],
  bgMuted: palette.gray[100],
  bgInverse: palette.gray[900],

  sidebarBg: palette.gray[0],
  sidebarItemHoverBg: palette.gray[50],
  sidebarItemActiveBg: palette.green[50],
  sidebarItemActiveAccent: palette.green[500],
  topbarBg: palette.gray[0],
  cardBg: palette.gray[0],

  textPrimary: palette.gray[900],
  textSecondary: palette.gray[600],
  textTertiary: palette.gray[500],
  textDisabled: palette.gray[400],
  textOnBrand: palette.gray[0],
  textOnInverse: palette.gray[0],

  borderSubtle: palette.gray[100],
  borderDefault: palette.gray[200],
  borderStrong: palette.gray[300],
  borderFocus: palette.green[500],

  success: palette.emerald[500],
  successSubtle: palette.emerald[50],
  warning: palette.amber[500],
  warningSubtle: palette.amber[50],
  danger: palette.red[500],
  dangerSubtle: palette.red[50],
  info: palette.blue[500],
  infoSubtle: palette.blue[50],

  statusDraft: palette.gray[500],
  statusReview: palette.amber[500],
  statusPublished: palette.emerald[500],
  statusArchived: palette.violet[500],

  shadow1: elevation[1],
  shadow2: elevation[2],
  shadow3: elevation[3],
  shadow4: elevation[4],
  shadow5: elevation[5],
};

export const semanticDark: SemanticPalette = {
  // WCAG AA optimized — green-400 (#4EA97A) on bgCanvas #0B1220 ≈ 7.1:1
  brand: palette.green[400],
  brandHover: palette.green[300],
  brandActive: palette.green[200],
  brandSubtle: 'rgba(78, 169, 122, 0.18)',
  brandContrast: palette.gray[950],

  bgCanvas: palette.gray[950],
  bgElevated: '#0F1A2B',
  bgSubtle: '#121E30',
  bgMuted: '#182438',
  bgInverse: palette.gray[0],

  sidebarBg: '#0A1322',
  sidebarItemHoverBg: 'rgba(255, 255, 255, 0.06)',
  sidebarItemActiveBg: 'rgba(78, 169, 122, 0.18)',
  sidebarItemActiveAccent: palette.green[400],
  topbarBg: '#0F1A2B',
  cardBg: '#0F1A2B',

  // textPrimary #F1F5F9 / bg #0B1220 ≈ 15:1, textSecondary #B6C2D2 ≈ 9.5:1, tertiary #8A9AAE ≈ 6.1:1 → AA pass
  textPrimary: '#F1F5F9',
  textSecondary: '#B6C2D2',
  textTertiary: '#8A9AAE',
  textDisabled: '#5A6878',
  textOnBrand: palette.gray[950],
  textOnInverse: palette.gray[900],

  borderSubtle: 'rgba(255, 255, 255, 0.08)',
  borderDefault: 'rgba(255, 255, 255, 0.14)',
  borderStrong: 'rgba(255, 255, 255, 0.22)',
  borderFocus: palette.green[400],

  success: palette.emerald[500],
  successSubtle: 'rgba(16, 185, 129, 0.14)',
  warning: palette.amber[500],
  warningSubtle: 'rgba(245, 158, 11, 0.14)',
  danger: palette.red[500],
  dangerSubtle: 'rgba(239, 68, 68, 0.14)',
  info: palette.blue[500],
  infoSubtle: 'rgba(59, 130, 246, 0.14)',

  statusDraft: palette.gray[400],
  statusReview: palette.amber[500],
  statusPublished: palette.emerald[500],
  statusArchived: palette.violet[500],

  shadow1: elevationDark[1],
  shadow2: elevationDark[2],
  shadow3: elevationDark[3],
  shadow4: elevationDark[4],
  shadow5: elevationDark[5],
};
